type Translate = (key: string) => string;

export interface RxPrintLine {
  display_order: number;
  drug_name: string;
  concentration?: string | null;
  pharmaceutical_form?: string | null;
  dose?: string | null;
  dose_unit?: string | null;
  route: string;
  frequency?: string | null;
  duration_days?: number | null;
  instructions?: string | null;
  is_controlled?: boolean;
}

export interface RxPrintContext {
  patient_name: string;
  species: string | null;
  breed: string | null;
  sex_label: string | null;
  age_label: string | null;
  pet_record_code: string | null;
  owner_name: string | null;
  next_appointment_label: string | null;
  weight_label: string | null;
  issued_date: string;
  vet_name: string;
  vet_license: string | null;
  visit_folio: string;
  rx_reference: string;
}

const LOGO_PATH = '/novapet_logo_ticket.png';
let cached_logo_data_uri: string | null | undefined;

/** Escape for safe interpolation into the print HTML (lines are user-entered). */
function esc(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

function dash(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? esc(trimmed) : '—';
}

function buildDrugHeadline(l: RxPrintLine): string {
  const bits: string[] = [];
  if (l.pharmaceutical_form) bits.push(esc(l.pharmaceutical_form));
  if (l.concentration) {
    bits.push(esc(l.concentration));
  } else if (l.dose && l.dose_unit) {
    bits.push(`${esc(l.dose)} ${esc(l.dose_unit)}`);
  }
  const paren = bits.length > 0 ? ` (${bits.join(' ')})` : '';
  return `<strong>${esc(l.drug_name)}</strong>${paren}`;
}

function buildAdminLine(l: RxPrintLine, t: Translate): string {
  const route_label = esc(t(`clinical.route_${l.route}`));
  const qty = l.dose ? `${esc(l.dose)}${l.dose_unit ? ` ${esc(l.dose_unit)}` : ''}` : '____';
  const freq = l.frequency ? esc(l.frequency) : '____';
  const days = l.duration_days != null ? String(l.duration_days) : '____';
  return `Administrar vía ${route_label}: ${qty} cada ${freq} durante ${days} días.`;
}

function buildLineHtml(l: RxPrintLine, index: number, t: Translate): string {
  const controlled = l.is_controlled
    ? `<span class="rx-ctrl">▲ ${esc(t('clinical.rx_doc_controlled'))}</span>`
    : '';
  const instructions = l.instructions
    ? `<p class="rx-instr">${esc(t('clinical.rx_doc_instructions'))}: ${esc(l.instructions)}</p>`
    : '';
  return `<li class="rx-item">
    <p class="rx-headline"><span class="rx-n">${index + 1}.-</span> ${buildDrugHeadline(l)} <span class="rx-use">Uso veterinario</span> ${controlled}</p>
    <hr class="rx-divider" />
    <p class="rx-admin">${buildAdminLine(l, t)}</p>
    ${instructions}
  </li>`;
}

async function resolveLogoDataUri(): Promise<string | null> {
  if (cached_logo_data_uri !== undefined) return cached_logo_data_uri;
  try {
    const response = await fetch(LOGO_PATH, { cache: 'force-cache' });
    if (!response.ok) {
      cached_logo_data_uri = null;
      return null;
    }
    const blob = await response.blob();
    cached_logo_data_uri = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('logo_read_failed'));
      reader.onload = () => {
        if (typeof reader.result === 'string') resolve(reader.result);
        else reject(new Error('logo_read_failed'));
      };
      reader.readAsDataURL(blob);
    });
    return cached_logo_data_uri;
  } catch {
    cached_logo_data_uri = null;
    return null;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function waitForPopupImages(popup: Window): Promise<void> {
  const images = Array.from(popup.document.images ?? []);
  if (images.length === 0) return;
  await Promise.race([
    Promise.all(
      images.map((image) =>
        image.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              image.addEventListener('load', () => resolve(), { once: true });
              image.addEventListener('error', () => resolve(), { once: true });
            }),
      ),
    ).then(() => undefined),
    delay(1400),
  ]);
}

function buildDocumentHtml(ctx: RxPrintContext, lines: RxPrintLine[], t: Translate, logo_data_uri: string | null): string {
  const ordered = [...lines].sort((a, b) => a.display_order - b.display_order);
  const line_html = ordered.map((l, i) => buildLineHtml(l, i, t)).join('');
  const species = ctx.species ? ` (${esc(ctx.species)})` : '';
  const logo_markup = logo_data_uri
    ? `<img class="logo" src="${esc(logo_data_uri)}" alt="Novapet" />`
    : '<p class="logo-fallback">NOVAPET</p>';

  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Receta — ${esc(ctx.patient_name)}</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #17313a;
    margin: 0;
    padding: 10mm;
    background: #fff;
  }
  .sheet {
    border: 2px solid #1f6d82;
    border-radius: 14px;
    padding: 14px 18px 18px;
  }
  .top-row {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    margin-bottom: 10px;
  }
  .date-box { font-size: 12px; }
  .date-box .lbl { color: #1f6d82; font-weight: 700; }
  .logo-wrap { text-align: center; }
  .logo { height: 62px; width: auto; }
  .logo-fallback { font-size: 20px; font-weight: 800; color: #1f6d82; margin: 0; text-align: center; }
  .patient-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 5px 18px;
    font-size: 12.5px;
    padding: 8px 2px 14px;
  }
  .patient-grid .lbl { color: #1f6d82; font-weight: 700; margin-right: 4px; }
  .patient-grid .owner { grid-column: span 2; }
  .rx-grid {
    border: 2px solid #1f6d82;
    border-radius: 12px;
    padding: 10px 16px 14px;
  }
  .rx-grid-title {
    text-align: center;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.4px;
    color: #1f6d82;
    background: #eaf4f6;
    margin: -10px -16px 12px;
    padding: 8px 16px;
    border-radius: 10px 10px 0 0;
    text-transform: uppercase;
  }
  ol.rx-list { list-style: none; padding: 0; margin: 0; min-height: 160px; }
  li.rx-item { margin-bottom: 14px; }
  .rx-headline { font-size: 13px; margin: 0 0 4px; }
  .rx-n { color: #666; }
  .rx-use { font-style: italic; color: #444; }
  .rx-ctrl { color: #b00020; font-size: 11px; font-weight: 700; margin-left: 6px; }
  .rx-divider { border: none; border-top: 1px solid #1f6d82; margin: 2px 0 6px; }
  .rx-admin { font-size: 12.5px; margin: 0; }
  .rx-instr { font-size: 11.5px; color: #444; font-style: italic; margin: 3px 0 0; }
  .empty { color: #777; font-size: 13px; text-align: center; padding: 20px 0; }
  .signature {
    margin-top: 18px;
    padding-top: 8px;
    border-top: 1px solid #1f6d82;
    font-size: 12px;
  }
  .signature .vet-name { font-weight: 700; margin: 0; }
  .signature .vet-license { margin: 2px 0 0; color: #333; }
  footer.rx-footer {
    position: relative;
    margin-top: 16px;
    text-align: center;
    font-size: 11px;
    color: #17313a;
    background: #dcedf0;
    border: 1px solid #1f6d82;
    border-radius: 12px;
    padding: 10px 44px 10px 16px;
  }
  footer.rx-footer p { margin: 3px 0; }
  footer.rx-footer .addr { font-weight: 700; }
  footer.rx-footer .phones {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-weight: 700;
    font-size: 13px;
  }
  footer.rx-footer .phones .rule {
    flex: 1;
    max-width: 70px;
    height: 0;
    border-top: 1px solid #1f6d82;
  }
  footer.rx-footer .social {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 14px;
    font-size: 10.5px;
    font-weight: 600;
  }
  footer.rx-footer .social .item { display: inline-flex; align-items: center; gap: 4px; }
  footer.rx-footer .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    border: 1px solid #1f6d82;
    font-size: 9px;
    line-height: 1;
    color: #1f6d82;
    flex: none;
  }
  .badge-24h {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: #6b4a3a;
    color: #fff;
    border: 3px solid #fff;
    box-shadow: 0 0 0 1px #1f6d82;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
  .badge-24h .n { font-weight: 800; font-size: 13px; }
  .badge-24h .s { font-size: 5px; font-weight: 700; letter-spacing: 0.3px; margin-top: 1px; }
  @media print {
    body { padding: 6mm; }
    @page { margin: 6mm; }
  }
</style>
</head>
<body>
  <div class="sheet">
    <div class="top-row">
      <div class="date-box"><span class="lbl">Fecha:</span> ${esc(ctx.issued_date)}</div>
      <div class="logo-wrap">${logo_markup}</div>
      <div></div>
    </div>

    <div class="patient-grid">
      <div><span class="lbl">Nombre:</span>${esc(ctx.patient_name)}${species}</div>
      <div><span class="lbl">Raza:</span>${dash(ctx.breed)}</div>
      <div><span class="lbl">Edad:</span>${dash(ctx.age_label)}</div>

      <div><span class="lbl">No. Exp.:</span>${dash(ctx.pet_record_code)}</div>
      <div><span class="lbl">Peso:</span>${dash(ctx.weight_label)}</div>
      <div><span class="lbl">Sexo:</span>${dash(ctx.sex_label)}</div>

      <div class="owner"><span class="lbl">Propietario:</span>${dash(ctx.owner_name)}</div>
      <div><span class="lbl">Prox. Cita:</span>${dash(ctx.next_appointment_label)}</div>
    </div>

    <div class="rx-grid">
      <p class="rx-grid-title">Hospital Veterinario de Especialidades</p>
      ${ordered.length === 0 ? `<p class="empty">${esc(t('clinical.no_rx_lines'))}</p>` : `<ol class="rx-list">${line_html}</ol>`}
      <div class="signature">
        <p class="vet-name">${esc(ctx.vet_name)}</p>
        <p class="vet-license">Céd. Prof.: ${dash(ctx.vet_license)}</p>
      </div>
    </div>

    <footer class="rx-footer">
      <p class="addr">Prolongación Cuauhtémoc #224 Huizache 1 C.P. 34160 Durango, Dgo.</p>
      <p class="phones"><span class="rule"></span>618 185.57.25 / 618 112.38.96<span class="rule"></span></p>
      <p class="social">
        <span class="item"><span class="icon">&#9993;</span>contacto@novapet.mx</span>
        <span class="item"><span class="icon">&#127760;</span>www.novapet.mx</span>
        <span class="item"><span class="icon">f</span>NovapetMx</span>
      </p>
      <div class="badge-24h"><span class="n">24</span><span class="s">URGENCIAS</span></div>
    </footer>
  </div>
</body>
</html>`;
}

/**
 * Opens a print-ready prescription document in a new window and triggers the browser
 * print dialog (the user can choose "Guardar como PDF" as the destination — same
 * no-external-dependency approach used by every other printable document in NovaPet).
 * Returns false if the popup was blocked so the caller can surface a message.
 */
export async function printPrescription(
  ctx: RxPrintContext,
  lines: RxPrintLine[],
  t: Translate,
): Promise<boolean> {
  const logo_data_uri = await resolveLogoDataUri();
  const html = buildDocumentHtml(ctx, lines, t, logo_data_uri);

  const w = window.open('', '_blank', 'width=820,height=960');
  if (!w) return false;
  w.opener = null; // sever the same-origin back-reference (handle stays usable)
  w.document.open();
  w.document.write(html);
  w.document.close();
  await waitForPopupImages(w);
  w.focus();
  w.print();
  return true;
}
